import React, { useEffect, useMemo, useState } from "react";
import { CalendarCheck, Repeat, Save } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, inputCls, Field } from "@/components/ui-kit";
import { toast } from "sonner";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const STATUSES = ["available", "unavailable", "maybe"];

function nextDates(count = 6, weekday = 6) {
  // upcoming Sundays by default (weekday 6 = Sunday in Mon=0 scheme)
  const out = [];
  const d = new Date();
  for (let i = 0; i < 60 && out.length < count; i++) {
    const dd = new Date(d); dd.setDate(d.getDate() + i);
    const wd = (dd.getDay() + 6) % 7; // convert Sun=0..Sat=6 to Mon=0..Sun=6
    if (wd === weekday) out.push(dd.toISOString().slice(0, 10));
  }
  return out;
}

export default function Availability() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [memberId, setMemberId] = useState("");
  const [recurring, setRecurring] = useState({});
  const [overrides, setOverrides] = useState({});
  const [grid, setGrid] = useState(null);
  const canManageOthers = ["super_admin", "worship_director", "worship_leader"].includes(user?.role);
  const dates = useMemo(() => nextDates(6, 6), []);

  useEffect(() => {
    api.get("/team").then((r) => {
      setMembers(r.data);
      const mine = user?.member_id || (r.data[0] && r.data[0].id);
      setMemberId(mine);
    });
  }, [user]);

  const loadGrid = () => {
    if (!canManageOthers) return;
    Promise.all(dates.map((d) => api.get(`/availability/resolve?date=${d}`).then((r) => [d, r.data]).catch(() => [d, {}])))
      .then((pairs) => setGrid(Object.fromEntries(pairs)));
  };
  useEffect(() => { loadGrid(); /* eslint-disable-next-line */ }, [canManageOthers]);

  useEffect(() => {
    if (!memberId) return;
    api.get(`/availability/recurring?member_id=${memberId}`).then((r) => {
      const map = {}; r.data.forEach((x) => (map[x.weekday] = x)); setRecurring(map);
    });
    api.get(`/availability?member_id=${memberId}`).then((r) => {
      const map = {}; r.data.forEach((x) => (map[x.date] = x.status)); setOverrides(map);
    });
  }, [memberId]);

  const setRule = async (weekday, status, after_time = "") => {
    await api.post("/availability/recurring", { member_id: memberId, weekday, status, after_time });
    setRecurring({ ...recurring, [weekday]: { weekday, status, after_time } });
    toast.success(`${WEEKDAYS[weekday]} rule saved`);
  };
  const clearRule = async (weekday) => {
    await api.delete(`/availability/recurring/${memberId}/${weekday}`);
    const r = { ...recurring }; delete r[weekday]; setRecurring(r);
  };
  const setOverride = async (date, status) => {
    await api.post("/availability", { member_id: memberId, date, status });
    setOverrides({ ...overrides, [date]: status }); toast.success("Availability updated");
  };

  return (
    <div>
      <PageHeader eyebrow="When you can serve" title="My Availability"
        subtitle="Set a recurring rule once — it applies to every future roster automatically. Override any specific date below." />

      {canManageOthers && (
        <div className="mb-5">
          <label className="text-xs text-[#8A97AC] mr-2">Editing for:</label>
          <select value={memberId} onChange={(e) => setMemberId(e.target.value)} data-testid="availability-member-select"
            className="bg-[#0B101D] border border-white/10 rounded-lg px-3 py-2 text-sm text-[#F7F5F0]">
            {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6 items-start">
        <div className="wf-card rounded-2xl p-5">
          <h3 className="font-display font-semibold text-[#F7F5F0] mb-4 flex items-center gap-2"><Repeat className="w-4 h-4 text-[#E5C378]" /> Recurring weekly rule</h3>
          <div className="space-y-2">
            {WEEKDAYS.map((d, wd) => {
              const rule = recurring[wd];
              return (
                <div key={d} className="flex items-center gap-2 bg-[#0B101D] border border-white/5 rounded-lg p-2.5">
                  <div className="w-10 text-sm text-[#B8C1D1] font-medium">{d}</div>
                  <select value={rule?.status || ""} onChange={(e) => (e.target.value ? setRule(wd, e.target.value, rule?.after_time || "") : clearRule(wd))}
                    data-testid={`recurring-${d}`} className="bg-[#0B101D] border border-white/10 rounded-lg px-2 py-1.5 text-sm text-[#F7F5F0] capitalize flex-1">
                    <option value="">No rule</option>{STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {rule?.status === "available" && (
                    <input type="time" value={rule?.after_time || ""} onChange={(e) => setRule(wd, "available", e.target.value)}
                      className="bg-[#0B101D] border border-white/10 rounded-lg px-2 py-1.5 text-sm text-[#F7F5F0]" title="Available after" />
                  )}
                </div>
              );
            })}
          </div>
          <p className="text-xs text-[#6E7D97] mt-3">Example: set Thursday → Available, after 18:00. Rosters will show you as available on future Thursdays.</p>
        </div>

        <div className="wf-card rounded-2xl p-5">
          <h3 className="font-display font-semibold text-[#F7F5F0] mb-4 flex items-center gap-2"><CalendarCheck className="w-4 h-4 text-[#E5C378]" /> Upcoming Sundays</h3>
          <div className="space-y-2">
            {dates.map((date) => (
              <div key={date} className="flex items-center justify-between bg-[#0B101D] border border-white/5 rounded-lg p-2.5">
                <span className="text-sm text-[#F7F5F0]">{new Date(date + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}</span>
                <div className="flex gap-1.5">
                  {STATUSES.map((st) => (
                    <button key={st} onClick={() => setOverride(date, st)} data-testid={`override-${date}-${st}`}
                      className={`px-2.5 py-1 rounded-lg text-xs font-medium border capitalize ${overrides[date] === st ? (st === "available" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" : st === "maybe" ? "bg-amber-500/15 text-amber-300 border-amber-500/40" : "bg-rose-500/15 text-rose-300 border-rose-500/40") : "bg-white/5 text-[#8A97AC] border-white/10"}`}>{st}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-[#6E7D97] mt-3">A specific date always overrides your recurring rule.</p>
        </div>
      </div>

      {canManageOthers && grid && (
        <div className="wf-card rounded-2xl p-5 mt-6" data-testid="team-availability-grid">
          <h3 className="font-display font-semibold text-[#F7F5F0] mb-4 flex items-center gap-2"><CalendarCheck className="w-4 h-4 text-[#E5C378]" /> Team availability — next 6 Sundays</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr>
                  <th className="text-left px-2 py-2 text-[#8A97AC] font-medium sticky left-0 bg-[#0E1524]">Member</th>
                  {dates.map((d) => (
                    <th key={d} className="px-2 py-2 text-[10px] text-[#8A97AC] font-medium whitespace-nowrap">{new Date(d + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.id} className="border-t border-white/5">
                    <td className="px-2 py-2 text-[#F7F5F0] whitespace-nowrap sticky left-0 bg-[#0E1524]">{m.name}</td>
                    {dates.map((d) => {
                      const st = grid[d]?.[m.id]?.status || "unknown";
                      const cls = st === "available" ? "bg-emerald-400" : st === "maybe" ? "bg-amber-400" : st === "unavailable" ? "bg-rose-400" : "bg-gray-600";
                      return <td key={d} className="px-2 py-2 text-center"><span title={st} className={`inline-block w-3 h-3 rounded-full ${cls}`} data-testid={`grid-${m.id}-${d}`} /></td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-3 mt-3 text-[10px] text-[#6E7D97]">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-400" /> available</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400" /> maybe</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-rose-400" /> unavailable</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gray-600" /> not set</span>
          </div>
        </div>
      )}
    </div>
  );
}
